import { createRoute } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Shield, Pencil } from "lucide-react";
import { rootRoute } from "./__root";
import { api } from "../lib/api";
import { useAuth } from "../lib/use-auth";
import { ALL_MODULES } from "@crm/shared/permissions";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Table } from "../components/ui/Table/Table";
import { Modal } from "../components/ui/Modal/Modal";
import { Button } from "../components/ui/Button/Button";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import { PasswordInput } from "../components/ui/PasswordInput/PasswordInput";
import { Select } from "../components/ui/Select/Select";
import { Badge } from "../components/ui/Badge/Badge";
import styles from "./AdminUsers.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/admin/users",
	component: withPermission(AdminUsersPage, "admin", "read"),
});

interface UserRow {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	role: string;
	isActive: boolean;
	locale: string;
	createdAt: string;
	permissions: PermissionRow[];
	phone: string | null;
	tcKimlikNo: string | null;
	sapSlpCode: number | null;
	sapSlpName: string | null;
}

interface SapSalesPersonOption {
	sapSlpCode: number;
	name: string;
	linkedUserId: string | null;
	linkedUserName: string | null;
}

interface PermissionRow {
	id: string;
	userId: string;
	module: string;
	canCreate: boolean;
	canRead: boolean;
	canUpdate: boolean;
	canDelete: boolean;
	canExport: boolean;
	canShare: boolean;
}

const MODULES = ALL_MODULES;

function sapSalesPersonOptions(
	people: SapSalesPersonOption[],
	currentUserId: string | null,
	currentCode: string,
	t: (key: string) => string,
) {
	const options = [
		{ value: "", label: t("sapSalesPersonNone") },
		...people.map((p) => {
			const base = `${p.name} (#${p.sapSlpCode})`;
			const linkedElsewhere = p.linkedUserId && p.linkedUserId !== currentUserId;
			return {
				value: String(p.sapSlpCode),
				label: linkedElsewhere ? `${base} — ${t("sapSalesPersonLinkedTo")}: ${p.linkedUserName}` : base,
			};
		}),
	];
	if (currentCode && !options.some((o) => o.value === currentCode)) {
		options.push({ value: currentCode, label: `#${currentCode}` });
	}
	return options;
}

const toSapSlpCode = (value: string) => (value === "" ? null : Number(value));

function AdminUsersPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const [users, setUsers] = useState<UserRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [createOpen, setCreateOpen] = useState(false);
	const [permUser, setPermUser] = useState<UserRow | null>(null);
	const [editUser, setEditUser] = useState<UserRow | null>(null);
	const [saving, setSaving] = useState(false);
	const [createError, setCreateError] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [editError, setEditError] = useState("");
	const [editFieldErrors, setEditFieldErrors] = useState<Record<string, string>>({});
	const [actionError, setActionError] = useState("");
	const [nameFilter, setNameFilter] = useState("");
	const [roleFilter, setRoleFilter] = useState("");
	const [sapPeople, setSapPeople] = useState<SapSalesPersonOption[]>([]);

	const [form, setForm] = useState({
		email: "",
		firstName: "",
		lastName: "",
		password: "",
		role: "user" as "admin" | "user" | "customer",
		phone: "",
		tcKimlikNo: "",
		sapSlpCode: "",
		createSapSalesPerson: false,
	});

	const loadSapPeople = useCallback(async () => {
		try {
			const res = await api.get<{ items: SapSalesPersonOption[] }>(
				"/api/admin/users/sap-salespersons",
			);
			setSapPeople(res.items);
		} catch {
			setSapPeople([]);
		}
	}, []);

	useEffect(() => {
		let cancelled = false;
		api
			.get<{ items: SapSalesPersonOption[] }>("/api/admin/users/sap-salespersons")
			.then((res) => {
				if (!cancelled) setSapPeople(res.items);
			})
			.catch(() => {
				if (!cancelled) setSapPeople([]);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	function validatePassword(password: string): string | null {
		if (password.length < 12) return t("passwordRequirements");
		if (!/[A-Z]/.test(password)) return t("passwordRequirements");
		if (!/[a-z]/.test(password)) return t("passwordRequirements");
		if (!/[0-9]/.test(password)) return t("passwordRequirements");
		if (!/[^A-Za-z0-9]/.test(password)) return t("passwordRequirements");
		return null;
	}

	const reloadUsers = useCallback(async () => {
		try {
			const res = await api.get<{ items: UserRow[] }>("/api/admin/users");
			setUsers(res.items);
		} catch {
			setUsers([]);
		}
	}, []);

	useEffect(() => {
		if (user && user.role !== "admin") {
			window.location.href = "/";
		}
	}, [user]);

	useEffect(() => {
		let cancelled = false;
		api
			.get<{ items: UserRow[] }>("/api/admin/users")
			.then((res) => {
				if (!cancelled) setUsers(res.items);
			})
			.catch(() => {
				if (!cancelled) setUsers([]);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [reloadUsers]);

	const handleCreate = async () => {
		setCreateError("");
		const errors: Record<string, string> = {};
		if (!form.email?.trim()) errors.email = t("requiredField");
		if (!form.firstName?.trim()) errors.firstName = t("requiredField");
		if (!form.lastName?.trim()) errors.lastName = t("requiredField");
		if (!form.password) errors.password = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		if (!form.email || !form.firstName || !form.lastName || !form.password) {
			setCreateError(t("fillRequiredFields"));
			return;
		}
		const passwordError = validatePassword(form.password);
		if (passwordError) {
			setCreateError(passwordError);
			return;
		}
		setSaving(true);
		try {
			await api.post("/api/admin/users", {
				...form,
				sapSlpCode: form.createSapSalesPerson ? null : toSapSlpCode(form.sapSlpCode),
			});
			setCreateOpen(false);
			setForm({
				email: "",
				firstName: "",
				lastName: "",
				password: "",
				role: "user",
				phone: "",
				tcKimlikNo: "",
				sapSlpCode: "",
				createSapSalesPerson: false,
			});
			setCreateError("");
			await Promise.all([reloadUsers(), loadSapPeople()]);
		} catch (err) {
			const message = err instanceof Error ? err.message : t("createUserFailed");
			setCreateError(message);
		}
		setSaving(false);
	};

	const handleEditSave = async (updates: {
		email: string;
		firstName: string;
		lastName: string;
		role: "admin" | "user" | "customer";
		phone: string;
		tcKimlikNo: string;
		sapSlpCode: number | null;
		newPassword?: string;
	}) => {
		if (!editUser) return;
		setEditError("");
		const errors: Record<string, string> = {};
		if (!updates.email?.trim()) errors.email = t("requiredField");
		if (!updates.firstName?.trim()) errors.firstName = t("requiredField");
		if (!updates.lastName?.trim()) errors.lastName = t("requiredField");
		setEditFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		setSaving(true);
		try {
			const { newPassword, ...profile } = updates;
			await api.patch(`/api/admin/users/${editUser.id}`, profile);
			if (newPassword) {
				await api.post(`/api/admin/users/${editUser.id}/reset-password`, { newPassword });
			}
			setEditUser(null);
			setEditError("");
			await Promise.all([reloadUsers(), loadSapPeople()]);
		} catch (err) {
			const message = err instanceof Error ? err.message : t("updateFailed");
			setEditError(message);
		}
		setSaving(false);
	};

	const toggleActive = async (u: UserRow) => {
		setActionError("");
		try {
			if (u.isActive) {
				await api.post(`/api/admin/users/${u.id}/disable`, {});
			} else {
				await api.post(`/api/admin/users/${u.id}/enable`, {});
			}
			await reloadUsers();
		} catch (err) {
			const message = err instanceof Error ? err.message : t("updateFailed");
			setActionError(message);
		}
	};

	const savePermissions = async (userId: string, perms: PermissionRow[]) => {
		setSaving(true);
		setActionError("");
		try {
			await api.put(`/api/admin/users/${userId}/permissions`, perms);
			setPermUser(null);
			await reloadUsers();
		} catch (err) {
			const message = err instanceof Error ? err.message : t("updateFailed");
			setActionError(message);
		}
		setSaving(false);
	};

	const filteredUsers = users.filter((u) => {
		const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
		if (nameFilter && !fullName.includes(nameFilter.toLowerCase())) return false;
		if (roleFilter && u.role !== roleFilter) return false;
		return true;
	});

	return (
		<div className={styles.container}>
			<PageHeader
				title={t("users")}
				action={
					<Button onClick={() => setCreateOpen(true)}>
						<Plus size={16} />
						{t("createUser")}
					</Button>
				}
			/>

			<div className={styles.filters}>
				<input
					className={styles.filterInput}
					placeholder="İsme göre ara..."
					value={nameFilter}
					onChange={(e) => setNameFilter(e.target.value)}
				/>
				<select
					className={styles.filterSelect}
					value={roleFilter}
					onChange={(e) => setRoleFilter(e.target.value)}
				>
					<option value="">Tüm Tipler</option>
					<option value="admin">{t("admin")}</option>
					<option value="user">{t("user")}</option>
					<option value="customer">{t("customer")}</option>
				</select>
			</div>

			{actionError && <div className={styles.pageError}>{actionError}</div>}

			<Table
				columns={[
					{
						key: "name",
						header: t("name"),
						render: (row) => `${row.firstName} ${row.lastName}`,
					},
					{
						key: "email",
						header: t("email"),
					},
					{
						key: "sapSlpCode",
						header: t("sapSalesPerson"),
						render: (row) =>
							row.sapSlpCode != null
								? `${row.sapSlpName ?? ""} (#${row.sapSlpCode})`.trim()
								: "—",
					},
					{
						key: "role",
						header: t("role"),
						render: (row) => t(row.role),
						width: "100px",
					},
					{
						key: "isActive",
						header: t("status"),
						render: (row) => (
							<Badge variant={row.isActive ? "success" : "danger"}>
								{row.isActive ? t("active") : t("inactive")}
							</Badge>
						),
						width: "100px",
					},
					{
						key: "actions",
						header: t("actions"),
						render: (row) => (
							<div className={styles.actionRow}>
								<Button variant="ghost" size="sm" onClick={() => setEditUser(row)}>
									<Pencil size={14} />
									{t("edit")}
								</Button>
								<Button variant="ghost" size="sm" onClick={() => setPermUser(row)}>
									<Shield size={14} />
									{t("permissions")}
								</Button>
								<Button
									variant={row.isActive ? "danger" : "secondary"}
									size="sm"
									onClick={() => toggleActive(row)}
								>
									{row.isActive ? t("disable") : t("enable")}
								</Button>
							</div>
						),
						width: "200px",
					},
				]}
				data={filteredUsers}
				isLoading={loading}
				emptyMessage={t("noData")}
				keyExtractor={(row) => row.id}
			/>

			<Modal
				isOpen={createOpen}
				onClose={() => {
					setCreateOpen(false);
					setCreateError("");
				}}
				title={t("createUser")}
			>
				{createError && <div className={styles.modalError}>{createError}</div>}
				<FormField label={t("email")} required>
					<Input
						type="email"
						value={form.email}
						onChange={(e) => setForm({ ...form, email: e.target.value })}
						error={fieldErrors.email}
					/>
				</FormField>
				<FormField label={t("firstName")} required>
					<Input
						type="text"
						value={form.firstName}
						onChange={(e) => setForm({ ...form, firstName: e.target.value })}
						error={fieldErrors.firstName}
					/>
				</FormField>
				<FormField label={t("lastName")} required>
					<Input
						type="text"
						value={form.lastName}
						onChange={(e) => setForm({ ...form, lastName: e.target.value })}
						error={fieldErrors.lastName}
					/>
				</FormField>
				<FormField label={t("role")}>
					<Select
						options={[
							{ value: "user", label: t("user") },
							{ value: "admin", label: t("admin") },
							{ value: "customer", label: t("customer") },
						]}
						value={form.role}
						onChange={(e) =>
							setForm({ ...form, role: e.target.value as "admin" | "user" | "customer" })
						}
					/>
				</FormField>
				<FormField label={t("phone")}>
					<Input
						type="text"
						value={form.phone}
						onChange={(e) => setForm({ ...form, phone: e.target.value })}
					/>
				</FormField>
				<FormField label={t("tcKimlikNo")}>
					<Input
						type="text"
						value={form.tcKimlikNo}
						onChange={(e) => setForm({ ...form, tcKimlikNo: e.target.value })}
					/>
				</FormField>
				<FormField label={t("sapSalesPerson")}>
					<label style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<input
							type="checkbox"
							checked={form.createSapSalesPerson}
							onChange={(e) =>
								setForm({ ...form, createSapSalesPerson: e.target.checked, sapSlpCode: "" })
							}
						/>
						<span>{t("sapSalesPersonCreateNew")}</span>
					</label>
					{!form.createSapSalesPerson && (
						<Select
							options={sapSalesPersonOptions(sapPeople, null, form.sapSlpCode, t)}
							value={form.sapSlpCode}
							onChange={(e) => setForm({ ...form, sapSlpCode: e.target.value })}
						/>
					)}
				</FormField>
				<FormField label={t("password")} required>
					<PasswordInput
						value={form.password}
						onChange={(e) => setForm({ ...form, password: e.target.value })}
						error={fieldErrors.password ?? ""}
					/>
					<span className={styles.hint}>{t("passwordRequirements")}</span>
				</FormField>
				<div className={styles.modalActions}>
					<Button variant="secondary" onClick={() => setCreateOpen(false)}>
						{t("cancel")}
					</Button>
					<Button onClick={handleCreate} isLoading={saving}>
						{t("create")}
					</Button>
				</div>
			</Modal>

			{permUser && (
				<PermissionModal
					user={permUser}
					onClose={() => setPermUser(null)}
					onSave={savePermissions}
					saving={saving}
					t={t}
				/>
			)}

			{editUser && (
				<EditUserModal
					user={editUser}
					onClose={() => {
						setEditUser(null);
						setEditError("");
						setEditFieldErrors({});
					}}
					onSave={handleEditSave}
					saving={saving}
					error={editError}
					fieldErrors={editFieldErrors}
					sapPeople={sapPeople}
					t={t}
				/>
			)}
		</div>
	);
}

function EditUserModal({
	user,
	onClose,
	onSave,
	saving,
	error,
	fieldErrors,
	sapPeople,
	t,
}: {
	user: UserRow;
	onClose: () => void;
	onSave: (updates: {
		email: string;
		firstName: string;
		lastName: string;
		role: "admin" | "user" | "customer";
		phone: string;
		tcKimlikNo: string;
		sapSlpCode: number | null;
		newPassword?: string;
	}) => void;
	saving: boolean;
	error: string;
	fieldErrors: Record<string, string>;
	sapPeople: SapSalesPersonOption[];
	t: (key: string) => string;
}) {
	const [form, setForm] = useState({
		email: user.email,
		firstName: user.firstName,
		lastName: user.lastName,
		role: user.role as "admin" | "user" | "customer",
		phone: user.phone ?? "",
		tcKimlikNo: user.tcKimlikNo ?? "",
		sapSlpCode: user.sapSlpCode != null ? String(user.sapSlpCode) : "",
		newPassword: "",
	});
	const [passwordError, setPasswordError] = useState("");

	function validatePassword(password: string): string | null {
		if (password.length < 12) return t("passwordRequirements");
		if (!/[A-Z]/.test(password)) return t("passwordRequirements");
		if (!/[a-z]/.test(password)) return t("passwordRequirements");
		if (!/[0-9]/.test(password)) return t("passwordRequirements");
		if (!/[^A-Za-z0-9]/.test(password)) return t("passwordRequirements");
		return null;
	}

	const handleSave = () => {
		setPasswordError("");
		if (form.newPassword) {
			const strengthError = validatePassword(form.newPassword);
			if (strengthError) {
				setPasswordError(strengthError);
				return;
			}
		}
		onSave({
			email: form.email,
			firstName: form.firstName,
			lastName: form.lastName,
			role: form.role,
			phone: form.phone,
			tcKimlikNo: form.tcKimlikNo,
			sapSlpCode: toSapSlpCode(form.sapSlpCode),
			...(form.newPassword ? { newPassword: form.newPassword } : {}),
		});
	};

	return (
		<Modal isOpen onClose={onClose} title={`${t("edit")}: ${user.firstName} ${user.lastName}`}>
			{error && <div className={styles.modalError}>{error}</div>}
			<FormField label={t("email")} required>
				<Input
					type="email"
					value={form.email}
					onChange={(e) => setForm({ ...form, email: e.target.value })}
					error={fieldErrors.email}
				/>
			</FormField>
			<FormField label={t("firstName")} required>
				<Input
					type="text"
					value={form.firstName}
					onChange={(e) => setForm({ ...form, firstName: e.target.value })}
					error={fieldErrors.firstName}
				/>
			</FormField>
			<FormField label={t("lastName")} required>
				<Input
					type="text"
					value={form.lastName}
					onChange={(e) => setForm({ ...form, lastName: e.target.value })}
					error={fieldErrors.lastName}
				/>
			</FormField>
			<FormField label={t("role")}>
				<Select
					options={[
						{ value: "user", label: t("user") },
						{ value: "admin", label: t("admin") },
						{ value: "customer", label: t("customer") },
					]}
					value={form.role}
					onChange={(e) =>
						setForm({ ...form, role: e.target.value as "admin" | "user" | "customer" })
					}
				/>
			</FormField>
			<FormField label={t("phone")}>
				<Input
					type="text"
					value={form.phone}
					onChange={(e) => setForm({ ...form, phone: e.target.value })}
				/>
			</FormField>
			<FormField label={t("tcKimlikNo")}>
				<Input
					type="text"
					value={form.tcKimlikNo}
					onChange={(e) => setForm({ ...form, tcKimlikNo: e.target.value })}
				/>
			</FormField>
			<FormField label={t("sapSalesPerson")}>
				<Select
					options={sapSalesPersonOptions(sapPeople, user.id, form.sapSlpCode, t)}
					value={form.sapSlpCode}
					onChange={(e) => setForm({ ...form, sapSlpCode: e.target.value })}
				/>
			</FormField>
			{passwordError && <div className={styles.modalError}>{passwordError}</div>}
			<FormField label={t("newPassword")}>
				<PasswordInput
					value={form.newPassword}
					onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
				/>
				<span className={styles.hint}>{t("passwordRequirements")}</span>
			</FormField>
			<div className={styles.modalActions}>
				<Button variant="secondary" onClick={onClose}>
					{t("cancel")}
				</Button>
				<Button onClick={handleSave} isLoading={saving}>
					{t("save")}
				</Button>
			</div>
		</Modal>
	);
}

function PermissionModal({
	user,
	onClose,
	onSave,
	saving,
	t,
}: {
	user: UserRow;
	onClose: () => void;
	onSave: (userId: string, perms: PermissionRow[]) => void;
	saving: boolean;
	t: (key: string) => string;
}) {
	const [perms, setPerms] = useState<PermissionRow[]>(() => {
		const existing = new Map(user.permissions.map((p) => [p.module, p]));
		return MODULES.map((module) => {
			const p = existing.get(module);
			if (p) return { ...p };
			return {
				id: "",
				userId: user.id,
				module,
				canCreate: false,
				canRead: false,
				canUpdate: false,
				canDelete: false,
				canExport: false,
				canShare: false,
			};
		});
	});

	const toggle = (index: number, key: keyof PermissionRow) => {
		setPerms((prev) => prev.map((p, i) => (i === index ? { ...p, [key]: !p[key] } : p)));
	};

	const setAll = (value: boolean) => {
		setPerms((prev) =>
			prev.map((p) => ({
				...p,
				canCreate: value,
				canRead: value,
				canUpdate: value,
				canDelete: value,
				canExport: value,
				canShare: value,
			})),
		);
	};

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`${t("permissions")}: ${user.firstName} ${user.lastName}`}
			maxWidth="min(95vw, 1100px)"
		>
			<div className={styles.permActions}>
				<Button variant="ghost" size="sm" onClick={() => setAll(true)}>
					{t("grantAll")}
				</Button>
				<Button variant="ghost" size="sm" onClick={() => setAll(false)}>
					{t("revokeAll")}
				</Button>
			</div>
			<div className={styles.permGrid}>
				<div className={styles.permGridHeader}>{t("module")}</div>
				<div className={styles.permGridHeader}>{t("create")}</div>
				<div className={styles.permGridHeader}>{t("read")}</div>
				<div className={styles.permGridHeader}>{t("update")}</div>
				<div className={styles.permGridHeader}>{t("delete")}</div>
				<div className={styles.permGridHeader}>{t("export")}</div>
				<div className={styles.permGridHeader}>{t("share")}</div>
				{perms.map((p, i) => (
					<>
						<div key={`${p.module}-name`} className={styles.permName}>
							{t(p.module)}
						</div>
						<div key={`${p.module}-c`} className={styles.permCell}>
							<input
								type="checkbox"
								checked={p.canCreate}
								onChange={() => toggle(i, "canCreate")}
							/>
						</div>
						<div key={`${p.module}-r`} className={styles.permCell}>
							<input type="checkbox" checked={p.canRead} onChange={() => toggle(i, "canRead")} />
						</div>
						<div key={`${p.module}-u`} className={styles.permCell}>
							<input
								type="checkbox"
								checked={p.canUpdate}
								onChange={() => toggle(i, "canUpdate")}
							/>
						</div>
						<div key={`${p.module}-d`} className={styles.permCell}>
							<input
								type="checkbox"
								checked={p.canDelete}
								onChange={() => toggle(i, "canDelete")}
							/>
						</div>
						<div key={`${p.module}-e`} className={styles.permCell}>
							<input
								type="checkbox"
								checked={p.canExport}
								onChange={() => toggle(i, "canExport")}
							/>
						</div>
						<div key={`${p.module}-s`} className={styles.permCell}>
							<input type="checkbox" checked={p.canShare} onChange={() => toggle(i, "canShare")} />
						</div>
					</>
				))}
			</div>
			<div className={styles.modalActions}>
				<Button variant="secondary" onClick={onClose}>
					{t("cancel")}
				</Button>
				<Button onClick={() => onSave(user.id, perms)} isLoading={saving}>
					{t("save")}
				</Button>
			</div>
		</Modal>
	);
}
